import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAuditLogs, getAuditStats } from '../../../store/actions/audit';
import {
  Container,
  Header,
  Title,
  Subtitle,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  FiltersContainer,
  FiltersGrid,
  FilterGroup,
  FilterLabel,
  FilterInput,
  FilterSelect,
  FilterActions,
  Button,
  LogsContainer,
  LogsTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableCell,
  ActionBadge,
  UserTypeBadge,
  EntityBadge,
  TimeStamp,
  Pagination,
  PageInfo,
  PageControls,
  LoadingContainer,
  LoadingText,
  EmptyState,
  EmptyIcon,
  EmptyText,
  EmptySubtext,
} from './styles';

const AuditLogs = () => {
  const dispatch = useDispatch();
  const auditData = useSelector(state => state.AuditData);
  const { logs = [], pagination = {}, stats = {}, loading = false, statsLoading = false } = auditData || {};

  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    action: '',
    user_type: '',
    entity_type: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    dispatch(getAuditStats());
    dispatch(getAuditLogs(filters));
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handleApplyFilters = () => {
    dispatch(getAuditLogs(filters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      page: 1,
      limit: 50,
      action: '',
      user_type: '',
      entity_type: '',
      start_date: '',
      end_date: '',
    };
    setFilters(clearedFilters);
    dispatch(getAuditLogs(clearedFilters));
  };

  const handlePageChange = (newPage) => {
    const updatedFilters = { ...filters, page: newPage };
    setFilters(updatedFilters);
    dispatch(getAuditLogs(updatedFilters));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container>
      <Header>
        <Title>Activity Logs</Title>
        <Subtitle>Track your company's activities and user actions</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatLabel>Total Logs</StatLabel>
          <StatValue>{statsLoading ? '...' : (stats.total_logs || 0).toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Today</StatLabel>
          <StatValue>{statsLoading ? '...' : (stats.logs_today || 0).toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>This Week</StatLabel>
          <StatValue>{statsLoading ? '...' : (stats.logs_this_week || 0).toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>This Month</StatLabel>
          <StatValue>{statsLoading ? '...' : (stats.logs_this_month || 0).toLocaleString()}</StatValue>
        </StatCard>
      </StatsGrid>

      <FiltersContainer>
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Action</FilterLabel>
            <FilterSelect
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="VIEW">View</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>User Type</FilterLabel>
            <FilterSelect
              value={filters.user_type}
              onChange={(e) => handleFilterChange('user_type', e.target.value)}
            >
              <option value="">All Users</option>
              <option value="company">Company</option>
              <option value="staff">Staff</option>
              <option value="rider">Rider</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Entity Type</FilterLabel>
            <FilterSelect
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            >
              <option value="">All Entities</option>
              <option value="staff">Staff</option>
              <option value="rider">Rider</option>
              <option value="order">Order</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Start Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>End Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>

        <FilterActions>
          <Button onClick={handleClearFilters}>Clear Filters</Button>
          <Button primary onClick={handleApplyFilters}>Apply Filters</Button>
        </FilterActions>
      </FiltersContainer>

      <LogsContainer>
        {loading ? (
          <LoadingContainer>
            <LoadingText>Loading activity logs...</LoadingText>
          </LoadingContainer>
        ) : logs.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📋</EmptyIcon>
            <EmptyText>No activity logs found</EmptyText>
            <EmptySubtext>Try adjusting your filters</EmptySubtext>
          </EmptyState>
        ) : (
          <>
            <LogsTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Time</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Entity</TableHeader>
                    <TableHeader>Description</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <TimeStamp>{formatDate(log.created_at)}</TimeStamp>
                      </TableCell>
                      <TableCell>
                        <div>{log.user_name}</div>
                        <UserTypeBadge>{log.user_type}</UserTypeBadge>
                      </TableCell>
                      <TableCell>
                        <ActionBadge action={log.action}>{log.action}</ActionBadge>
                      </TableCell>
                      <TableCell>
                        <EntityBadge>{log.entity_type}</EntityBadge>
                      </TableCell>
                      <TableCell>{log.description}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </LogsTable>

            <Pagination>
              <PageInfo>
                Showing {(((pagination.page || 1) - 1) * (pagination.limit || 50)) + 1} to{' '}
                {Math.min((pagination.page || 1) * (pagination.limit || 50), pagination.total || 0)} of{' '}
                {pagination.total || 0} logs
              </PageInfo>
              <PageControls>
                <Button
                  onClick={() => handlePageChange((pagination.page || 1) - 1)}
                  disabled={(pagination.page || 1) === 1}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => handlePageChange((pagination.page || 1) + 1)}
                  disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
                >
                  Next
                </Button>
              </PageControls>
            </Pagination>
          </>
        )}
      </LogsContainer>
    </Container>
  );
};

export default AuditLogs;
